import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import FormWrapper from "../../../pagecomponents/Common/FormWrapper.js";
import { getStatementData } from "../../../redux/slices/MasterIncomeStatementSlice";
import { Table, Button } from "react-bootstrap";
import NoData from "../../../pagecomponents/Common/NoData.js";
import { checkEmpty } from "../../../helpers/utility.js";

const BreakUpModal = ({ type, start_date, end_date, setFooterFlag }) => {
  const dispatch = useDispatch();
  const PAGE_SIZE = 100;

  const [searchedData, setSearchedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const searchIncomeStatement = useCallback(async (page = 1, append = false) => {
    setIsLoading(true);
    const params = {
      start_date,
      end_date,
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
      type,
    };

    const { payload } = await dispatch(getStatementData({ inputData: params }));
    if (payload?.status === "error") {
      if (!append) {
        setSearchedData([]);
      }
      setFooterFlag(false);
      setHasMore(false);
      setIsLoading(false);
    } else {
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      setSearchedData((prev) => {
        const merged = append ? [...prev, ...rows] : rows;
        checkEmpty(merged) ? setFooterFlag(false) : setFooterFlag(true);
        return merged;
      });

      const totalCount = Number(payload?.count || 0);
      const hasNextByCount = totalCount > 0 && page * PAGE_SIZE < totalCount;
      const hasNextByRows = rows.length >= PAGE_SIZE;
      setHasMore(hasNextByCount || hasNextByRows);
      setCurrentPage(page);
      setIsLoading(false);
    }
  }, [dispatch, end_date, setFooterFlag, start_date, type]);

  useEffect(() => {
    if (checkEmpty(type)) {
      setSearchedData([]);
      setCurrentPage(1);
      setHasMore(false);
      setFooterFlag(false);
      return;
    }

    setSearchedData([]);
    setCurrentPage(1);
    setHasMore(false);
    searchIncomeStatement(1, false);
  }, [searchIncomeStatement, setFooterFlag, type]);

  const handleLoadMore = () => {
    if (isLoading || !hasMore) return;
    searchIncomeStatement(currentPage + 1, true);
  };

  const renderTableHead = () => {
    if (type === "journal") {
      return (
        <tr>
          <th className="text-start col-6">Particulars</th>
          <th className="text-start col-6">Type</th>
          <th className="text-end col-6">Amount</th>
        </tr>
      );
    } else if (type === "Cost of Revenue" || type === "revenue") {
      return (
        <tr>
          <th className="text-start col-6">Date</th>
          <th className="text-start col-6">Charge Name</th>
          <th className="text-start col-6">Charge Quantity</th>
          <th className="text-start col-6">Charge Value</th>
          <th className="text-end col-6">Amount</th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th className="text-start col-6">Date</th>
          <th className="text-start col-6">Particulars</th>
          <th className="text-start col-6">Vch No</th>
          <th className="text-start col-6">Type</th>
          <th className="text-end col-6">Amount</th>
        </tr>
      );
    }
  };

  const renderTableRow = (item, index) => {
    if (type === "journal") {
      return (
        <tr key={index}>
          <td className="text-start col-6">{item.voucher_payment_Data}</td>
          <td className="text-start col-6">{item.voucher_payment_Type}</td>
          <td className="text-end col-6">{item.voucher_payment_Amount}</td>
        </tr>
      );
    } else if (type === "Cost of Revenue" || type === "revenue") {
      return (
        <tr key={index}>
          <td className="text-start col-6">{item.created_at?.split(" ")[0]}</td>
          <td className="text-start col-6">{item.charge_Name}</td>
          <td className="text-end col-6">{item.charge_Qty}</td>
          <td className="text-end col-6">{item.charge_Unit_Amount}</td>
          <td className="text-end col-6">{item.charge_value}</td>
        </tr>
      );
    } else {
      return (
        <tr key={index}>
          <td className="text-start col-6">{item.created_at?.split(" ")[0]}</td>
          <td className="text-start col-6">{item.voucher_payment_Data}</td>
          <td className="text-start col-6">{item.voucher_No}</td>
          <td className="text-start col-6">{item.voucher_payment_Type}</td>
          <td className="text-end col-6">{item.voucher_payment_Amount}</td>
        </tr>
      );
    }
  };

  return (
    <FormWrapper>
      <div style={{ maxHeight: "450px", overflowY: "auto" }}>
        {!isLoading && checkEmpty(searchedData) ? (
          <NoData />
        ) : (
          <>
            <Table className="table table-bordered text-nowrap mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 2,
                }}
              >
                {renderTableHead()}
              </thead>
              <tbody>
                {searchedData?.map((item, index) => renderTableRow(item, index))}
              </tbody>
            </Table>
            <div className="d-flex flex-column align-items-center py-2 gap-2">
              <small className="text-muted">
                Showing {searchedData.length} rows
                {hasMore ? " - click Load more to fetch the next batch." : "."}
              </small>
              {hasMore && (
                <Button type="button" size="sm" onClick={handleLoadMore} disabled={isLoading}>
                  {isLoading ? "Loading..." : "Load more"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </FormWrapper>
  );
};

export default BreakUpModal;
